import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { timeTrackingService } from '../services/timeTrackingService';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import './TimeTracking.css';

interface AgentStats {
  agent: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  };
  totalMinutes: number;
  totalHours: number;
  ticketCount: number;
  dailyBreakdown: Array<{
    day: number;
    minutes: number;
    hours: number;
    tickets: any[];
  }>;
  entries: any[];
}

interface CompanyAnalysis {
  companyName: string;
  totalMinutes: number;
  totalHours: number;
  ticketCount: number;
  agentCount: number;
  tickets: Array<{
    ticketNumber: string;
    title: string;
    minutes: number;
    hours: number;
    createdBy: string;
  }>;
  agents: string[]; // Array of agent IDs
}

const TimeTracking: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [stats, setStats] = useState<AgentStats[]>([]);
  const [companyAnalysis, setCompanyAnalysis] = useState<CompanyAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedAgent, setSelectedAgent] = useState<AgentStats | null>(null);
  const [detailsDialogVisible, setDetailsDialogVisible] = useState(false);
  const [monthName, setMonthName] = useState('');

  useEffect(() => {
    loadStats();
  }, [selectedDate]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();
      
      const data = await timeTrackingService.getAgentTimeStats(month, year);
      setStats(data.agents || []);
      setCompanyAnalysis(data.companyAnalysis || []);
      // Force English month name regardless of backend locale
      const enMonth = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long' });
      setMonthName(enMonth);
    } catch (err) {
      console.error('Error loading time stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const showAgentDetails = (agent: AgentStats) => {
    setSelectedAgent(agent);
    setDetailsDialogVisible(true);
  };

  // Format time helper - prikazuje sate i minute u formatu "1h 30min", "2h 15min"
  const formatTime = (hours: number, minutesRemainder: number): string => {
    if (hours === 0) {
      return `${minutesRemainder}min`;
    } else if (minutesRemainder === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutesRemainder}min`;
    }
  };

  // Helper za konverziju decimalnih sati u sate i minute
  const convertDecimalHours = (decimalHours: number): { hours: number; minutes: number } => {
    const totalMinutes = Math.floor(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes };
  };

  // Template za kolonu sa agentom
  const agentTemplate = (rowData: AgentStats) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="agent-avatar" style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981, #059669)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '1.1rem'
        }}>
          {rowData.agent.firstName.charAt(0)}{rowData.agent.lastName.charAt(0)}
        </div>
        <div>
          <div style={{ fontWeight: 600, color: '#111827' }}>
            {rowData.agent.firstName} {rowData.agent.lastName}
          </div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {rowData.agent.email}
          </div>
        </div>
      </div>
    );
  };

  // Template za ukupno vreme
  const timeTemplate = (rowData: AgentStats) => {
    const { hours, minutes } = convertDecimalHours(rowData.totalHours);
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>
          {formatTime(hours, minutes)}
        </div>
      </div>
    );
  };

  // Template za broj tiketa
  const ticketCountTemplate = (rowData: AgentStats) => {
    return (
      <div style={{ textAlign: 'center' }}>
        <Badge value={rowData.ticketCount} severity="info" />
      </div>
    );
  };

  // Template za uloge
  const roleTemplate = (rowData: AgentStats) => {
    const roleMap: any = {
      'AGENT': { label: 'Agent', severity: 'warning' },
      'SUPER_ADMIN': { label: 'Super Admin', severity: 'success' }
    };
    const config = roleMap[rowData.agent.role] || { label: rowData.agent.role, severity: 'info' };
    
    return <Badge value={config.label} severity={config.severity} />;
  };

  // Template za akcije
  const actionsTemplate = (rowData: AgentStats) => {
    return (
      <Button
        label="Details"
        icon="pi pi-eye"
        className="p-button-sm p-button-info"
        onClick={() => showAgentDetails(rowData)}
      />
    );
  };

  if (user?.role !== 'SUPER_ADMIN') {
    return (
      <div className="time-tracking-container">
        <Message severity="warn" text="Only SUPER_ADMIN can access this page" />
      </div>
    );
  }

  return (
    <div className="time-tracking-container">
      <div className="time-tracking-header">
        <div>
          <h1>⏱️ Time Tracking - Time Statistics</h1>
          <p className="subtitle">Monitoring working time of agents and administrators</p>
        </div>
        <div className="header-controls">
          <div className="calendar-container">
            <Calendar
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.value as Date)}
              view="month"
              dateFormat="MM/yy"
              placeholder="Select month"
              showIcon={false}
            />
            <i className="pi pi-calendar calendar-icon"></i>
          </div>
          <Button
            label="Refresh"
            icon="pi pi-refresh"
            onClick={loadStats}
            className="submit-btn"
          />
        </div>
      </div>

      {loading ? (
        <Card>
          <Skeleton height="400px" />
        </Card>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="summary-cards">
            <Card className="summary-card">
              <div className="summary-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                <i className="pi pi-users"></i>
              </div>
              <div className="summary-content">
                <div className="summary-value">{stats.length}</div>
                <div className="summary-label">Active Agents</div>
              </div>
            </Card>

            <Card className="summary-card">
              <div className="summary-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
                <i className="pi pi-clock"></i>
              </div>
              <div className="summary-content">
                <div className="summary-value">
                  {(() => {
                    const { hours, minutes } = convertDecimalHours(stats.reduce((sum, agent) => sum + agent.totalHours, 0));
                    return formatTime(hours, minutes);
                  })()}
                </div>
                <div className="summary-label">Total Hours</div>
              </div>
            </Card>

            <Card className="summary-card">
              <div className="summary-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
                <i className="pi pi-ticket"></i>
              </div>
              <div className="summary-content">
                <div className="summary-value">
                  {stats.reduce((sum, agent) => sum + agent.ticketCount, 0)}
                </div>
                <div className="summary-label">Resolved Tickets</div>
              </div>
            </Card>
          </div>

          {/* Company Analysis */}
          {companyAnalysis.length > 0 && (
            <Card className="company-analysis-card">
              <div className="card-header-custom">
                <h3>Company Analysis - {monthName} {selectedDate.getFullYear()}</h3>
                {/* <p className="card-subtitle">Billable vreme za naplatu</p> */}
              </div>
              <div className="company-grid">
                {companyAnalysis.map((company, index) => (
                  <div key={index} className="company-card">
                    <div className="company-header">
                      <div className="company-icon">
                        <i className="pi pi-building"></i>
                      </div>
                      <div className="company-info">
                        <h4>{company.companyName}</h4>
                        <p>{company.agentCount} Agents</p>
                      </div>
                    </div>
                    <div className="company-stats">
                      <div className="stat-item">
                        <span className="stat-value">{(() => {
                          const { hours, minutes } = convertDecimalHours(company.totalHours);
                          return formatTime(hours, minutes);
                        })()}</span>
                        <span className="stat-label">Billable Hours</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value">{company.ticketCount}</span>
                        <span className="stat-label">Tickets</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value">{company.agentCount}</span>
                        <span className="stat-label">Agents</span>
                      </div>
                    </div>
                    <div className="company-tickets">
                      <h5>Tickets:</h5>
                      {company.tickets.slice(0, 3).map((ticket, ticketIndex) => (
                        <div key={ticketIndex} className="ticket-item">
                          <div className="ticket-info">
                            <span className="ticket-number">{ticket.ticketNumber}</span>
                            <span className="ticket-title">{ticket.title}</span>
                            <span className="ticket-creator">- {ticket.createdBy}</span>
                          </div>
                          <span className="ticket-time">{(() => {
                            const { hours, minutes } = convertDecimalHours(ticket.hours);
                            return formatTime(hours, minutes);
                          })()}</span>
                        </div>
                      ))}
                      {company.tickets.length > 3 && (
                        <div className="ticket-item more-tickets">
                          <span className="more-text">+ {company.tickets.length - 3} more tickets...</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Agent Statistics Table */}
          <Card className="table-card">
            <div className="card-header-custom">
              <h3> Agent Statistics - {monthName} {selectedDate.getFullYear()}</h3>
            </div>
            {isMobile ? (
              <div className="list-cards time-tracking-cards">
                {stats.map((agent) => {
                  const { hours, minutes } = convertDecimalHours(agent.totalHours);
                  return (
                    <div key={agent.agent._id} className="list-card">
                      <div className="list-card-header">
                        <div>
                          <div className="list-card-title">{agent.agent.firstName} {agent.agent.lastName}</div>
                          <div className="list-card-sub">{agent.agent.email}</div>
                        </div>
                        {roleTemplate(agent)}
                      </div>
                      <div className="list-card-meta">
                        <span><b>{formatTime(hours, minutes)}</b></span>
                        <span>{agent.ticketCount} tickets</span>
                      </div>
                      <div className="list-card-actions">
                        {actionsTemplate(agent)}
                      </div>
                    </div>
                  );
                })}
                {stats.length === 0 && (
                  <div className="mobile-empty-state">
                    <i className="pi pi-clock" />
                    <h3>No data</h3>
                    <p>No data for the selected month.</p>
                  </div>
                )}
              </div>
            ) : (
            <DataTable
              value={stats}
              paginator
              rows={10}
              emptyMessage="No data for the selected month"
              className="modern-table"
              stripedRows
            >
              <Column 
                header="Agent" 
                body={agentTemplate}
                sortable
                sortField="agent.firstName"
              />
              <Column 
                header="Role" 
                body={roleTemplate}
                style={{ width: '150px', textAlign: 'center' }}
              />
              <Column 
                header="Total Time" 
                body={timeTemplate}
                sortable
                sortField="totalHours"
                style={{ width: '150px' }}
              />
              <Column 
                header="Tickets Count" 
                body={ticketCountTemplate}
                sortable
                sortField="ticketCount"
                style={{ width: '120px' }}
              />
              <Column 
                header="Actions" 
                body={actionsTemplate}
                style={{ width: '120px', textAlign: 'center' }}
              />
            </DataTable>
            )}
          </Card>
        </>
      )}

      {/* Agent Details Dialog */}
      <Dialog
        header={selectedAgent ? `Total Overview - ${selectedAgent.agent.firstName} ${selectedAgent.agent.lastName}` : ''}
        visible={detailsDialogVisible}
        style={{ width: 'min(800px, calc(100vw - 1.5rem))' }}
        onHide={() => setDetailsDialogVisible(false)}
        maximizable
      >
        {selectedAgent && (
          <div className="agent-details">
            {/* Agent Info */}
            <div className="agent-info-box">
              <div className="info-row">
                <span className="info-label">Agent:</span>
                <span className="info-value">
                  {selectedAgent.agent.firstName} {selectedAgent.agent.lastName}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Role:</span>
                <Badge 
                  value={selectedAgent.agent.role === 'AGENT' ? 'Agent' : 'Super Admin'} 
                  severity={selectedAgent.agent.role === 'AGENT' ? 'warning' : 'success'} 
                />
              </div>
              <div className="info-row">
                <span className="info-label">Total time:</span>
                <span className="info-value highlight">{(() => {
                  const { hours, minutes } = convertDecimalHours(selectedAgent.totalHours);
                  return formatTime(hours, minutes);
                })()}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Tickets total:</span>
                <span className="info-value">{selectedAgent.ticketCount}</span>
              </div>
            </div>

            {/* Daily Breakdown */}
            <div className="daily-breakdown">
              <h4>Daily Overview - {monthName} {selectedDate.getFullYear()}</h4>
              
              {selectedAgent.dailyBreakdown.length === 0 ? (
                <Message severity="info" text="No recorded time in this month" />
              ) : (
                <div className="daily-list">
                  {selectedAgent.dailyBreakdown.map((day) => (
                    <div key={day.day} className="daily-item">
                      <div className="daily-header">
                        <div className="daily-date">
                          <i className="pi pi-calendar"></i>
                          <span>{day.day}. {monthName}</span>
                        </div>
                        <div className="daily-time">
                          <i className="pi pi-clock"></i>
                          <span>{(() => {
                            const { hours, minutes } = convertDecimalHours(day.hours);
                            return formatTime(hours, minutes);
                          })()}</span>
                        </div>
                      </div>
                      
                      <div className="daily-tickets">
                        {day.tickets.map((ticket: any, idx: number) => (
                          <div key={idx} className="ticket-time-item">
                            <div className="ticket-info">
                              <i className="pi pi-ticket"></i>
                              <span className="ticket-number">{ticket.ticketNumber}</span>
                              <span className="ticket-title">{ticket.ticketTitle}</span>
                            </div>
                            <div className="ticket-duration">
                              <Badge value={`${ticket.minutes} min`} severity="success" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default TimeTracking;

